// BP 03 — workspace (team) membership client. Lists the workspaces the user
// belongs to (for the switcher) and manages members (owner/admin only).

import { api } from './apiClient';

export type WorkspaceRole = 'owner' | 'admin' | 'member';

export interface Workspace {
  id: string;
  name: string;
  role: WorkspaceRole;
  is_home: boolean;
}

export interface WorkspaceInvite {
  id: string;
  email: string;
  role: WorkspaceRole;
  expires_at: string;
}

export interface WorkspaceMember {
  user_id: string;
  role: WorkspaceRole;
  status: 'active' | 'invited' | 'suspended';
  email: string | null;
  display_name: string | null;
}

export const workspaceApi = {
  async listMine(): Promise<Workspace[]> {
    const r = await api.get('/api/v1/workspaces');
    return (r.data.workspaces || []) as Workspace[];
  },

  async listMembers(orgId: string): Promise<{ members: WorkspaceMember[]; myRole: WorkspaceRole }> {
    const r = await api.get(`/api/v1/workspaces/${orgId}/members`);
    return { members: r.data.members || [], myRole: r.data.my_role };
  },

  async addMember(orgId: string, email: string, role: WorkspaceRole): Promise<WorkspaceMember> {
    const r = await api.post(`/api/v1/workspaces/${orgId}/members`, { email, role });
    return r.data.member;
  },

  async updateMember(orgId: string, userId: string, role: WorkspaceRole): Promise<void> {
    await api.patch(`/api/v1/workspaces/${orgId}/members/${userId}`, { role });
  },

  async removeMember(orgId: string, userId: string): Promise<void> {
    await api.delete(`/api/v1/workspaces/${orgId}/members/${userId}`);
  },

  async create(name: string): Promise<Workspace> {
    const r = await api.post('/api/v1/workspaces', { name });
    return r.data.workspace as Workspace;
  },

  async listInvites(orgId: string): Promise<WorkspaceInvite[]> {
    const r = await api.get(`/api/v1/workspaces/${orgId}/invites`);
    return r.data.invites || [];
  },

  async invite(orgId: string, email: string, role: WorkspaceRole): Promise<{ invite: WorkspaceInvite; email_delivered: boolean }> {
    const r = await api.post(`/api/v1/workspaces/${orgId}/invites`, { email, role });
    return r.data;
  },

  async revokeInvite(orgId: string, inviteId: string): Promise<void> {
    await api.delete(`/api/v1/workspaces/${orgId}/invites/${inviteId}`);
  },

  async acceptInvite(token: string): Promise<{ ok?: boolean; requiresLogin?: boolean; workspace?: Workspace; email?: string; workspaceName?: string }> {
    const r = await api.get(`/api/v1/workspaces/accept-invite?token=${encodeURIComponent(token)}`);
    return r.data;
  },
};

// i18next KEYS, not display text — this file has no React context to call
// useTranslation() itself, so consumers render via t(WS_ROLE_LABELS[role]).
export const WS_ROLE_LABELS: Record<WorkspaceRole, string> = {
  owner: 'common:workspaceRoles.owner',
  admin: 'common:workspaceRoles.admin',
  member: 'common:workspaceRoles.member',
};

export const WS_ROLE_ORDER: WorkspaceRole[] = ['member', 'admin', 'owner'];
